import { useState, useEffect, useRef } from "react";
import { Calendar, ChevronDown, X } from "lucide-react";

/**
 * Reusable date-range filter with preset buttons + custom range picker.
 *
 * Emits `{ start, end, label }` on change where start/end are ISO strings
 * (or null when the preset is "all time"). The parent owns the state.
 *
 * Designed to drop into any page header — see TimeClockPage.js for usage.
 */

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// Sunday = 0, so for a US-style "week starts Sunday" simply subtract dayOfWeek
const startOfWeek = (d) => {
  const x = startOfDay(d);
  return addDays(x, -x.getDay());
};

const startOfMonth = (d) => {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
};

const toDateInput = (d) => {
  // YYYY-MM-DD for <input type="date">
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const fromDateInput = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "last_7", label: "Last 7 days" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_30", label: "Last 30 days" },
  { key: "all", label: "All time" },
];

export const computeRange = (key, customStart, customEnd) => {
  const now = new Date();
  if (key === "today") {
    return { start: startOfDay(now), end: endOfDay(now), label: "Today" };
  }
  if (key === "yesterday") {
    const y = addDays(now, -1);
    return { start: startOfDay(y), end: endOfDay(y), label: "Yesterday" };
  }
  if (key === "this_week") {
    return { start: startOfWeek(now), end: endOfDay(now), label: "This week" };
  }
  if (key === "last_week") {
    const sow = startOfWeek(now);
    return {
      start: addDays(sow, -7),
      end: new Date(sow.getTime() - 1),
      label: "Last week",
    };
  }
  if (key === "last_7") {
    return { start: startOfDay(addDays(now, -6)), end: endOfDay(now), label: "Last 7 days" };
  }
  if (key === "this_month") {
    return { start: startOfMonth(now), end: endOfDay(now), label: "This month" };
  }
  if (key === "last_month") {
    const som = startOfMonth(now);
    const lastDayPrev = new Date(som.getTime() - 1);
    return {
      start: startOfMonth(lastDayPrev),
      end: lastDayPrev,
      label: "Last month",
    };
  }
  if (key === "last_30") {
    return { start: startOfDay(addDays(now, -29)), end: endOfDay(now), label: "Last 30 days" };
  }
  if (key === "custom") {
    const s = customStart ? startOfDay(customStart) : null;
    const e = customEnd ? endOfDay(customEnd) : null;
    const label = s && e
      ? `${toDateInput(s)} → ${toDateInput(e)}`
      : "Custom range";
    return { start: s, end: e, label };
  }
  return { start: null, end: null, label: "All time" };
};

export default function DateRangeFilter({ value, onChange, defaultPreset = "this_week" }) {
  // value: { preset, customStart, customEnd } — parent-owned
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const preset = value?.preset || defaultPreset;
  const customStart = value?.customStart || null;
  const customEnd = value?.customEnd || null;

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const apply = (next) => {
    const merged = { preset, customStart, customEnd, ...next };
    const range = computeRange(
      merged.preset,
      merged.customStart,
      merged.customEnd
    );
    onChange?.({ ...merged, ...range });
  };

  const current = computeRange(preset, customStart, customEnd);
  const display = preset === "custom" && (!customStart || !customEnd)
    ? "Custom range…"
    : (PRESETS.find((p) => p.key === preset)?.label || current.label);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="date-range-trigger"
        className="flex items-center gap-2 h-10 px-4 rounded-full border border-slate-200 bg-white hover:border-slate-300 text-sm font-medium text-slate-700 transition-colors"
      >
        <Calendar className="w-4 h-4 text-slate-500" strokeWidth={1.75} />
        <span data-testid="date-range-label">{display}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-2 right-0 w-[420px] bg-white rounded-2xl border border-slate-200 shadow-xl p-4" data-testid="date-range-popover">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Quick presets</p>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-md hover:bg-slate-100 text-slate-400"
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-4">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => { apply({ preset: p.key }); setOpen(false); }}
                data-testid={`preset-${p.key}`}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors text-left ${
                  preset === p.key
                    ? "bg-rose-500 text-white"
                    : "bg-slate-50 hover:bg-slate-100 text-slate-700"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="pt-3 border-t border-slate-100">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Custom range</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-slate-500 font-medium">From</label>
                <input
                  type="date"
                  value={customStart ? toDateInput(customStart) : ""}
                  onChange={(e) => apply({ preset: "custom", customStart: fromDateInput(e.target.value) })}
                  data-testid="custom-start-input"
                  className="w-full mt-0.5 h-9 px-2.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-rose-400"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-medium">To</label>
                <input
                  type="date"
                  value={customEnd ? toDateInput(customEnd) : ""}
                  onChange={(e) => apply({ preset: "custom", customEnd: fromDateInput(e.target.value) })}
                  data-testid="custom-end-input"
                  className="w-full mt-0.5 h-9 px-2.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-rose-400"
                />
              </div>
            </div>
            {preset === "custom" && (!customStart || !customEnd) && (
              <p className="text-[10px] text-amber-600 mt-2">Pick a start and end date to apply.</p>
            )}
            {preset === "custom" && customStart && customEnd && (
              <button
                onClick={() => setOpen(false)}
                data-testid="apply-custom-range"
                className="w-full mt-3 h-9 rounded-lg bg-rose-500 text-white text-xs font-semibold hover:bg-rose-600"
              >
                Apply range
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
